import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { hittingLine, pitchingLine, fmtDate, cnt, rate3, inningsFromOuts, EMDASH } from "@/components/player/statFormat";
import { CalendarDays, MapPin, Trophy, PlayCircle, ArrowLeft, ChevronRight, Flag, X } from "lucide-react";

const homeAwayLabel = { home: "vs", away: "@", neutral: "vs (N)" };

function LineRow({ l }) {
  const hl = hittingLine(l.hitting);
  const pl = pitchingLine(l.pitching);
  const fielding = (l.fielding || []).filter((f) => cnt(f.total_chances) > 0);
  return (
    <div data-testid={`box-line-${l.player_id}`} className="border border-slate-200 rounded-sm p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          {l.slug ? (
            <Link to={`/prospects/${l.slug}`} className="font-semibold text-[#0A192F] hover:text-[#E63946]">{l.name}</Link>
          ) : (
            <span className="font-semibold text-[#0A192F]">{l.name}</span>
          )}
          {l.positions_played?.length > 0 && <span className="text-xs text-slate-400 ml-2">{l.positions_played.join(", ")}</span>}
        </div>
        {l.slug && <Link to={`/prospects/${l.slug}`} className="text-xs text-[#E63946] font-semibold uppercase tracking-wider inline-flex items-center gap-1 shrink-0">Profile <ChevronRight className="w-3.5 h-3.5" /></Link>}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
        {hl && <div><span className="text-[10px] uppercase tracking-wider text-slate-400 mr-2">Hitting</span><span className="font-data">{hl}</span></div>}
        {pl && <div><span className="text-[10px] uppercase tracking-wider text-slate-400 mr-2">Pitching</span><span className="font-data">{pl}</span></div>}
        {fielding.length > 0 && <div><span className="text-[10px] uppercase tracking-wider text-slate-400 mr-2">Fielding</span><span className="font-data">{fielding.map((f) => `${f.position} ${cnt(f.po)}PO/${cnt(f.a)}A/${cnt(f.e)}E`).join(", ")}</span></div>}
      </div>
      {l.public_recap && <p className="mt-2 text-xs text-slate-600 italic">"{l.public_recap}"</p>}
      {(l.video_url || l.highlight_url) && (
        <a href={l.video_url || l.highlight_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A192F] hover:text-[#E63946]">
          <PlayCircle className="w-4 h-4" /> Watch Highlight
        </a>
      )}
    </div>
  );
}

export default function PublicGame() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    api.get(`/games/${id}/public-box`)
      .then((r) => { setData(r.data); setState("ok"); document.title = "Game Box Score | CBR Prospects"; })
      .catch(() => setState("notfound"));
    return () => { document.title = "CBR Prospects"; };
  }, [id]);

  if (state === "loading") return <PublicLayout><div className="max-w-4xl mx-auto p-10 label-overline">Loading game…</div></PublicLayout>;
  if (state === "notfound") return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto p-10">
        <div className="font-display text-3xl uppercase">Game not found</div>
        <Link to="/prospects" className="text-[#E63946] text-sm underline mt-3 inline-block">← All prospects</Link>
      </div>
    </PublicLayout>
  );

  const g = data.game || {};
  const lines = data.lines || [];
  const scoreStr = g.us != null && g.them != null ? `${g.us} – ${g.them}` : null;
  const outcomeText = g.outcome === "W" ? "Win" : g.outcome === "L" ? "Loss" : g.outcome === "T" ? "Tie" : null;

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
          <button onClick={() => window.history.back()} data-testid="game-back" className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white uppercase tracking-wider mb-6">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider text-white/60">
            {g.game_type && <span className="border border-white/20 px-2 py-0.5">{g.game_type}</span>}
            {g.status && <span className="border border-white/20 px-2 py-0.5">{g.status}</span>}
            {g.home_away && <span className="border border-white/20 px-2 py-0.5">{g.home_away}</span>}
          </div>
          <h1 data-testid="game-matchup" className="font-display text-3xl lg:text-5xl uppercase tracking-tighter mt-3 leading-tight">
            {g.team_name || "CBR"} <span className="text-white/40 text-2xl lg:text-3xl">{homeAwayLabel[g.home_away] || "vs"}</span> <span className="text-[#E63946]">{g.opponent_name || "Opponent"}</span>
          </h1>
          {scoreStr && (
            <div className="mt-3 flex items-center gap-3">
              <span data-testid="game-score" className="font-data text-4xl">{scoreStr}</span>
              {outcomeText && <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${g.outcome === "W" ? "bg-emerald-600" : g.outcome === "L" ? "bg-[#E63946]" : "bg-slate-500"}`}>{outcomeText}</span>}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/70">
            <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {fmtDate(g.date)}{g.time ? ` · ${g.time}` : ""}</span>
            {g.venue && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {g.venue}</span>}
            {g.tournament_name && <span className="inline-flex items-center gap-1.5"><Trophy className="w-4 h-4 text-[#E63946]" /> {g.tournament_name}</span>}
            {g.opponent_team_slug && <Link to={`/live/teams/${g.opponent_team_slug}`} className="underline hover:text-white">Opponent page →</Link>}
          </div>
        </div>
      </section>

      <section className="bg-white py-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="label-overline text-[#E63946]">Verified CBR Game Data</div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-display text-2xl uppercase tracking-tight mt-1 mb-4">Player Stat Lines</h2>
            <button data-testid="report-error-open" onClick={() => setReportOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-[#E63946]">
              <Flag className="w-3.5 h-3.5" /> Report an Error
            </button>
          </div>
          {lines.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-500">No public stat lines available for this game yet.</div>
          ) : (
            <div className="space-y-3" data-testid="game-lines">
              {lines.map((l) => <LineRow key={l.player_id} l={l} />)}
            </div>
          )}
        </div>
      </section>
      {reportOpen && <ReportErrorModal gameId={id} onClose={() => setReportOpen(false)} />}
    </PublicLayout>
  );
}

function ReportErrorModal({ gameId, onClose }) {
  const [f, setF] = useState({ field: "score", current_value: "", suggested_value: "", reason: "", reporter_name: "", reporter_email: "", evidence_url: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/live/games/${gameId}/report-error`, f);
      setSent(true);
    } catch { /* noop */ } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="report-error-modal">
      <div className="bg-white rounded-sm max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-xl uppercase tracking-tight">Report a Statistical Error</div>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        {sent ? (
          <div className="text-sm text-slate-600 py-4" data-testid="report-error-sent">Thanks — your report was submitted to CBR for review. Official records are never changed automatically.</div>
        ) : (
          <div className="space-y-3">
            <label className="block text-xs">What is incorrect?
              <select data-testid="report-field" value={f.field} onChange={(e) => set("field", e.target.value)} className="w-full mt-1 border border-slate-300 rounded-sm px-2 py-2 text-sm">
                {["score", "player stats", "opponent", "lineup", "missing player", "position", "result"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Current value" value={f.current_value} onChange={(e) => set("current_value", e.target.value)} className="border border-slate-300 rounded-sm px-2 py-2 text-sm" />
              <input placeholder="Suggested value" value={f.suggested_value} onChange={(e) => set("suggested_value", e.target.value)} className="border border-slate-300 rounded-sm px-2 py-2 text-sm" />
            </div>
            <textarea data-testid="report-reason" placeholder="Describe the error*" value={f.reason} onChange={(e) => set("reason", e.target.value)} className="w-full border border-slate-300 rounded-sm px-2 py-2 text-sm h-20" />
            <input placeholder="Evidence link (optional)" value={f.evidence_url} onChange={(e) => set("evidence_url", e.target.value)} className="w-full border border-slate-300 rounded-sm px-2 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Your name" value={f.reporter_name} onChange={(e) => set("reporter_name", e.target.value)} className="border border-slate-300 rounded-sm px-2 py-2 text-sm" />
              <input placeholder="Your email" value={f.reporter_email} onChange={(e) => set("reporter_email", e.target.value)} className="border border-slate-300 rounded-sm px-2 py-2 text-sm" />
            </div>
            <button data-testid="report-submit" onClick={submit} disabled={busy || !f.reason.trim()} className="w-full bg-[#E63946] text-white py-2.5 rounded-sm text-sm font-semibold uppercase tracking-wider disabled:opacity-50">{busy ? "Submitting…" : "Submit Report"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
